import React, { useEffect, useMemo, useState } from "react";
import { Smartphone, Wifi, Signal, SignalHigh, SignalMedium, SignalLow, CheckCircle2, AlertTriangle, ArrowRight, BatteryCharging } from "lucide-react";
import { api, getSessionId } from "../lib/api";

// ----- Platform detection -----
function detectPlatform() {
  if (typeof navigator === "undefined") return { isMobile: false, os: "unknown" };
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  return {
    isMobile: isIOS || isAndroid,
    isIOS,
    isAndroid,
    os: isIOS ? "ios" : isAndroid ? "android" : "desktop",
  };
}

function readNavigatorConnection() {
  if (typeof navigator === "undefined") return null;
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return null;
  return {
    effectiveType: c.effectiveType || null, // 'slow-2g'|'2g'|'3g'|'4g'
    downlink: typeof c.downlink === "number" ? c.downlink : null, // Mbps est.
    rtt: typeof c.rtt === "number" ? c.rtt : null, // ms
    saveData: !!c.saveData,
    type: c.type || null, // 'wifi'|'cellular'|... (rare)
  };
}

// ----- Inferring radio from measured numbers -----
function inferRadio({ downloadMbps, uploadMbps, pingMs, connInfo, platform }) {
  // If nav.connection exposes a type, trust it first
  if (connInfo?.type === "wifi") return { label: "Wi-Fi", conf: "high", icon: "wifi" };
  if (connInfo?.type === "cellular" && connInfo?.effectiveType === "4g") {
    // Could be LTE or 5G — use measured speeds
    if (downloadMbps >= 250) return { label: "5G", conf: "medium", icon: "signal-high" };
    if (downloadMbps >= 100) return { label: "LTE-A / Low-band 5G", conf: "medium", icon: "signal-high" };
    return { label: "4G LTE", conf: "high", icon: "signal-medium" };
  }
  if (connInfo?.effectiveType === "3g") return { label: "3G", conf: "high", icon: "signal-low" };
  if (connInfo?.effectiveType === "slow-2g" || connInfo?.effectiveType === "2g") return { label: "2G / Edge", conf: "high", icon: "signal-low" };

  // iOS / missing API: heuristic from measurements
  if (downloadMbps == null && pingMs == null) return { label: "Unknown", conf: "low", icon: "signal" };
  if (downloadMbps != null) {
    if (downloadMbps >= 400 && pingMs != null && pingMs < 25) return { label: "Wi-Fi (fiber/cable)", conf: "high", icon: "wifi" };
    if (downloadMbps >= 200 && pingMs != null && pingMs < 35) {
      return platform?.isMobile
        ? { label: "5G (sub-6)", conf: "medium", icon: "signal-high" }
        : { label: "Wi-Fi", conf: "medium", icon: "wifi" };
    }
    if (downloadMbps >= 80) return platform?.isMobile ? { label: "LTE-A / Low-band 5G", conf: "medium", icon: "signal-high" } : { label: "Wi-Fi", conf: "medium", icon: "wifi" };
    if (downloadMbps >= 20) return platform?.isMobile ? { label: "4G LTE", conf: "medium", icon: "signal-medium" } : { label: "Wi-Fi (weak)", conf: "low", icon: "wifi" };
    if (downloadMbps >= 3) return { label: "3G / Weak LTE", conf: "medium", icon: "signal-low" };
    return { label: "Very slow / 2G", conf: "high", icon: "signal-low" };
  }
  return { label: "Unknown", conf: "low", icon: "signal" };
}

// ----- Carrier detection -----
const CARRIERS = [
  { id: "tmobile", patterns: ["T-Mobile", "TMO", "Sprint"], name: "T-Mobile",
    tips: [
      "T-Mobile has broad 5G Ultra Capacity (2.5 GHz) coverage. In Settings → Cellular → Voice & Data, set '5G On' (not 'Auto') to lock to 5G when available.",
      "If you see only 2–3 bars of 5G UC, move near a window or outdoors — the 2.5 GHz band loses 10–20 dB through walls.",
    ],
  },
  { id: "verizon", patterns: ["Verizon", "Cellco", "VZW"], name: "Verizon",
    tips: [
      "Verizon 5G Ultra Wideband (C-Band + mmWave) is dramatically faster than their low-band 5G. Look for '5G UW' icon in the status bar.",
      "mmWave 5G needs line of sight — if you're indoors, move near a window facing the street.",
    ],
  },
  { id: "att", patterns: ["AT&T", "Cingular", "ATT"], name: "AT&T",
    tips: [
      "AT&T's '5G+' (mmWave/C-Band) is way faster than plain '5G'. If the icon shows only '5G', you're on low-band — capped around 100 Mbps.",
      "Toggle airplane mode off/on to force a tower refresh if speeds seem stuck below your plan.",
    ],
  },
  { id: "rogers", patterns: ["Rogers"], name: "Rogers", tips: ["In Canada, Rogers 5G+ requires a compatible device and a 5G plan. Check Settings → Mobile Data → Voice & Data."] },
  { id: "bell", patterns: ["Bell"], name: "Bell Canada", tips: ["Bell 5G+ is fastest on 3500 MHz bands — typically near downtown cores and highways."] },
  { id: "vodafone", patterns: ["Vodafone"], name: "Vodafone", tips: ["Enable 5G in Settings — some plans keep it disabled by default to save battery."] },
  { id: "jio", patterns: ["Jio"], name: "Jio",
    tips: [
      "Jio True 5G (SA) is faster than LTE when available. Settings → SIMs → Preferred network → 5G.",
      "If you're stuck at LTE speeds, try toggling airplane mode to force a 5G re-registration.",
    ],
  },
  { id: "airtel", patterns: ["Bharti", "Airtel"], name: "Airtel", tips: ["Airtel 5G Plus auto-selects — ensure Settings → Mobile Network → Preferred is '5G/LTE/3G/2G'."] },
];

function detectCarrier(isp) {
  if (!isp) return null;
  const s = isp.toLowerCase();
  for (const c of CARRIERS) {
    if (c.patterns.some((p) => s.includes(p.toLowerCase()))) return c;
  }
  return null;
}

// ----- Tips engine -----
function buildTips({ radio, platform, connInfo, carrier, downloadMbps, pingMs }) {
  const tips = [];

  // Carrier-specific first
  if (carrier && platform.isMobile) {
    for (const t of carrier.tips) tips.push({ tag: carrier.name, text: t, tone: "info" });
  }

  // Data saver
  if (connInfo?.saveData) {
    tips.push({
      tag: "Low Data Mode",
      text: platform.isIOS
        ? "iOS Low Data Mode is ON — it throttles background refresh, iCloud sync and video quality. Settings → Cellular → [Your line] → Low Data Mode → Off."
        : "Your browser has Data Saver enabled, which throttles background loads. Turn it off in your browser settings for full speed.",
      tone: "warn",
    });
  }

  // iOS-specific tips
  if (platform.isIOS) {
    if (/^5G/i.test(radio.label)) {
      tips.push({ tag: "iOS", text: "You're on 5G. If speeds feel low, try: Settings → Cellular → Voice & Data → 5G On (instead of 5G Auto). 5G Auto deprioritises 5G to save battery.", tone: "info" });
    } else if (/LTE|4G/i.test(radio.label)) {
      tips.push({ tag: "iOS", text: "Connected on LTE. If 5G is available in your area, go to Settings → Cellular → Voice & Data → 5G On. If you don't see 5G, your plan or region may not include it.", tone: "info" });
    }
    if (/Wi-Fi/i.test(radio.label) && downloadMbps != null && downloadMbps < 50) {
      tips.push({ tag: "iOS", text: "Wi-Fi speed is lower than typical. Try toggling Wi-Fi off/on, or test on cellular — if cellular is faster, your router or modem is the bottleneck.", tone: "warn" });
    }
  }

  // Android-specific
  if (platform.isAndroid) {
    if (connInfo?.effectiveType === "4g" && downloadMbps != null && downloadMbps < 30) {
      tips.push({ tag: "Android", text: "Connected on 4G but getting slow speeds. Try: Settings → Network → SIMs → Preferred network type → 5G/LTE/3G (auto).", tone: "warn" });
    }
    tips.push({ tag: "Android", text: "Toggle Airplane mode on for 5 seconds then off — forces your phone to reconnect to the strongest tower.", tone: "info" });
  }

  // Universal tips
  if (downloadMbps != null && downloadMbps < 15 && /LTE|4G|3G/i.test(radio.label)) {
    tips.push({ tag: "Signal", text: "Speed is low for cellular. Move near a window or outside — concrete, metal and low-E glass block cell signal by 20–30 dB.", tone: "warn" });
  }
  if (pingMs != null && pingMs > 80 && platform.isMobile) {
    tips.push({ tag: "Latency", text: `Ping is high (${pingMs} ms). If you're gaming or on a video call, try switching between Wi-Fi and cellular — whichever is closer to the source typically wins.`, tone: "info" });
  }
  if (/Wi-Fi/i.test(radio.label) && platform.isMobile) {
    tips.push({ tag: "Wi-Fi", text: "On Wi-Fi? If it's public/guest Wi-Fi, speeds are often throttled. Testing on cellular is a good second opinion.", tone: "info" });
  }

  return tips.slice(0, 6);
}

// ----- Component -----
export function MobileAdvisor({ testid = "mobile-advisor" }) {
  const [platform, setPlatform] = useState({ isMobile: false, os: "unknown" });
  const [connInfo, setConnInfo] = useState(null);
  const [geo, setGeo] = useState(null);
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    setPlatform(detectPlatform());
    setConnInfo(readNavigatorConnection());
    // Poll once more after 800ms — some browsers populate lazily
    const t = setTimeout(() => setConnInfo(readNavigatorConnection()), 800);
    api.get("/geo").then((r) => setGeo(r.data)).catch(() => {});
    api.get("/speedtest/history", { params: { session_id: getSessionId(), limit: 1 } })
      .then((r) => setLatest((r.data && r.data[0]) || null))
      .catch(() => {});
    return () => clearTimeout(t);
  }, []);

  const radio = useMemo(() =>
    inferRadio({
      downloadMbps: latest?.download_mbps,
      uploadMbps: latest?.upload_mbps,
      pingMs: latest?.ping_ms,
      connInfo,
      platform,
    }),
  [latest, connInfo, platform]);

  const carrier = useMemo(() => detectCarrier(geo?.isp), [geo]);
  const tips = useMemo(() => buildTips({
    radio,
    platform,
    connInfo,
    carrier,
    downloadMbps: latest?.download_mbps,
    pingMs: latest?.ping_ms,
  }), [radio, platform, connInfo, carrier, latest]);

  const IconMap = { wifi: Wifi, signal: Signal, "signal-high": SignalHigh, "signal-medium": SignalMedium, "signal-low": SignalLow };
  const RadioIcon = IconMap[radio.icon] || Signal;

  return (
    <section className="glass rounded-2xl p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-[rgba(61,107,255,0.12)] border border-[rgba(61,107,255,0.25)]">
            <Smartphone className="w-4 h-4 text-[var(--cobalt-bright)]" />
          </div>
          <h3 className="font-display text-base sm:text-lg font-semibold truncate">Mobile connection advisor</h3>
        </div>
      </div>

      {/* Detection panel */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <Info label="Device" value={platform.isIOS ? "iPhone / iPad" : platform.isAndroid ? "Android" : "Desktop / other"} testid="adv-device" />
        <Info label="Connection" value={radio.label} icon={<RadioIcon className="w-4 h-4 text-[var(--cobalt-bright)]" />} testid="adv-radio" />
        <Info label="Carrier / ISP" value={carrier?.name || geo?.isp || "Detecting…"} testid="adv-carrier" />
        <Info label="Data saver" value={connInfo?.saveData ? "On" : "Off"} testid="adv-saver" warn={connInfo?.saveData} />
        {connInfo?.effectiveType && <Info label="API type" value={connInfo.effectiveType.toUpperCase()} testid="adv-etype" />}
        {connInfo?.downlink != null && <Info label="Est. downlink" value={`${connInfo.downlink} Mbps`} testid="adv-downlink" />}
        {connInfo?.rtt != null && <Info label="Est. RTT" value={`${connInfo.rtt} ms`} testid="adv-rtt" />}
        {latest && <Info label="Last test" value={`${Number(latest.download_mbps).toFixed(0)} / ${Number(latest.upload_mbps).toFixed(0)} Mbps`} testid="adv-last" />}
      </div>

      {/* Not mobile nudge */}
      {!platform.isMobile && (
        <div className="rounded-xl p-4 border border-white/10 bg-white/5 text-sm text-[var(--text-dim)] mb-5" data-testid="adv-desktop-note">
          Looks like you're on a desktop browser. Open this page on your phone to get cellular-specific tips (5G vs LTE, carrier settings, etc.).
        </div>
      )}

      {/* Tips */}
      {tips.length === 0 ? (
        <div className="text-sm text-[var(--text-muted)]" data-testid="adv-no-tips">
          Everything looks good — we don't have anything to suggest right now.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="adv-tips">
          {tips.map((t, i) => (
            <li
              key={i}
              data-testid={`adv-tip-${i}`}
              className="flex gap-3 p-3 rounded-lg border"
              style={
                t.tone === "warn"
                  ? { background: "rgba(251,191,36,0.08)", borderColor: "rgba(251,191,36,0.28)" }
                  : { background: "rgba(61,107,255,0.06)", borderColor: "rgba(61,107,255,0.22)" }
              }
            >
              <div className="w-7 h-7 shrink-0 rounded-md flex items-center justify-center mt-0.5" style={{ background: t.tone === "warn" ? "rgba(251,191,36,0.15)" : "rgba(61,107,255,0.15)", border: `1px solid ${t.tone === "warn" ? "rgba(251,191,36,0.35)" : "rgba(61,107,255,0.32)"}` }}>
                {t.tone === "warn" ? <AlertTriangle className="w-3.5 h-3.5 text-amber-300" /> : <ArrowRight className="w-3.5 h-3.5 text-[var(--cobalt-bright)]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-muted)] mb-1">{t.tag}</div>
                <div className="text-sm leading-relaxed">{t.text}</div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* iOS API caveat */}
      {platform.isIOS && (
        <p className="mt-4 text-[11px] text-[var(--text-muted)] font-mono leading-relaxed">
          Note: iOS Safari doesn't expose the Network Information API, so the radio above is <em>inferred</em> from your speed test. For exact radio details, check iOS Settings → Cellular → Current Period.
        </p>
      )}
    </section>
  );
}

function Info({ label, value, icon, testid, warn }) {
  return (
    <div className="rounded-lg p-3 border border-white/8 bg-[rgba(15,17,32,0.5)] min-w-0" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-muted)] mb-1 flex items-center gap-1.5">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className={`text-sm font-medium truncate ${warn ? "text-amber-300" : "text-white"}`} title={typeof value === "string" ? value : ""}>
        {value}
      </div>
    </div>
  );
}
